import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { BookOpen, Plus, Trash2 } from '../../../components/icons';
import { fetchJournalEntries, createJournalEntry, updateJournalEntry, deleteJournalEntry, type JournalEntry } from '../../../lib/progress';
import { LoadingSpinner } from '../../../components/LoadingSpinner';

const MOODS = ['😊', '😌', '😐', '😔', '😤', '🤔', '🙏', '🔥'];

export function JournalViewTR() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [form, setForm] = useState({ title: '', content: '', mood: '' });

  useEffect(() => {
    if (!user) return;
    fetchJournalEntries(user.id).then(data => {
      setEntries(data);
      if (data.length > 0) setSelectedEntry(data[0]);
    }).finally(() => setIsLoading(false));
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !form.title.trim() || !form.content.trim()) return;
    if (editingEntry) {
      await updateJournalEntry(editingEntry.id, form);
      const updated = { ...editingEntry, ...form };
      setEntries(prev => prev.map(e => e.id === editingEntry.id ? updated : e));
      setSelectedEntry(updated);
    } else {
      const created = await createJournalEntry(user.id, form);
      if (created) {
        setEntries(prev => [created, ...prev]);
        setSelectedEntry(created);
      }
    }
    setShowForm(false);
    setEditingEntry(null);
    setForm({ title: '', content: '', mood: '' });
  };

  const handleEdit = (entry: JournalEntry) => {
    setEditingEntry(entry);
    setForm({ title: entry.title, content: entry.content, mood: entry.mood || '' });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    await deleteJournalEntry(id);
    setEntries(prev => {
      const next = prev.filter(e => e.id !== id);
      setSelectedEntry(next[0] || null);
      return next;
    });
  };

  const handleNew = () => {
    setEditingEntry(null);
    setForm({ title: '', content: '', mood: '' });
    setShowForm(true);
  };

  if (isLoading) return <LoadingSpinner message="Günlük yükleniyor..." />;

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="freud-heading-xl text-chocolate-900">Günlük</h1>
            <p className="text-sm text-chocolate-500">{entries.length} yazı</p>
          </div>
        </div>
        <button onClick={handleNew} className="freud-btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Yeni Yazı
        </button>
      </div>

      {showForm && (
        <div className="freud-card-elevated p-6 mb-6 max-w-2xl">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4">{editingEntry ? 'Yazıyı Düzenle' : 'Yeni Yazı'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="freud-label">Başlık *</label>
              <input className="freud-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Aklınızda ne var?" required />
            </div>
            <div>
              <label className="freud-label">Ruh Hali</label>
              <div className="flex gap-2 flex-wrap">
                {MOODS.map(m => (
                  <button key={m} type="button" onClick={() => setForm(p => ({ ...p, mood: p.mood === m ? '' : m }))}
                    className={`text-xl p-1.5 rounded-lg transition-all ${form.mood === m ? 'bg-sage-100 scale-110' : 'hover:bg-cream-100'}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="freud-label">İçerik *</label>
              <textarea className="freud-input" rows={6} value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} placeholder="Özgürce yazın..." required />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="freud-btn-primary">{editingEntry ? 'Değişiklikleri Kaydet' : 'Yazıyı Kaydet'}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditingEntry(null); }} className="freud-btn-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {entries.length === 0 && !showForm ? (
        <div className="freud-card-elevated p-12 text-center max-w-2xl">
          <BookOpen className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-chocolate-500">Henüz günlük yazısı yok. Yazmaya başlayın.</p>
        </div>
      ) : entries.length > 0 && (
        <div className="grid lg:grid-cols-[280px_1fr] gap-4 max-w-5xl">
          <div className="space-y-2">
            {entries.map(entry => (
              <button
                key={entry.id}
                onClick={() => setSelectedEntry(entry)}
                className={`w-full text-left p-3 rounded-xl transition-all ${selectedEntry?.id === entry.id ? 'bg-sage-100 ring-1 ring-sage-300' : 'freud-card hover:bg-cream-50'}`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  {entry.mood && <span className="text-base">{entry.mood}</span>}
                  <span className="text-xs text-chocolate-400">{new Date(entry.created_at).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <p className="text-sm font-medium text-chocolate-800 truncate">{entry.title}</p>
                <p className="text-xs text-chocolate-500 truncate mt-0.5">{entry.content}</p>
              </button>
            ))}
          </div>

          {selectedEntry && (
            <div className="freud-card-elevated p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {selectedEntry.mood && <span className="text-xl">{selectedEntry.mood}</span>}
                    <span className="text-sm text-chocolate-400">{new Date(selectedEntry.created_at).toLocaleDateString('tr-TR', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <h2 className="freud-heading-xl text-chocolate-900">{selectedEntry.title}</h2>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(selectedEntry)} className="p-2 rounded-lg text-chocolate-400 hover:bg-cream-100 hover:text-chocolate-700 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  </button>
                  <button onClick={() => handleDelete(selectedEntry.id)} className="p-2 rounded-lg text-chocolate-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="prose prose-sm max-w-none text-chocolate-700 whitespace-pre-wrap leading-relaxed">
                {selectedEntry.content}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
